import Phaser from 'phaser';
import { HUB_MENU_TRACK } from '../../config/levels';
import { originShopShelf, type OriginShopId, type OriginShopTower } from '../../config/originShop';
import { TOWER_NAMES, TOWER_TUTORIALS } from '../../config/displayNames';
import { availableTowers } from '../../config/unlocks';
import type { ProgressService } from '../../services/progressService';
import { DARKCOIN_SPIN_FRAMES, LIGHTCOIN_SPIN_FRAMES, spinCoin } from '../coin';
import { getAudioService } from '../audio/AudioService';
import { getMusicService } from '../audio/MusicService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { GOLD_HEX, hubProgress } from '../hubShared';
import { saveStorage } from '../saveStorage';
import { sweepAndToastAchievements } from '../../ui/achievementToast';

/**
 * The Origin Event's two shops (spec 2026-10-12), opened from the buttons
 * under the chapter cards on the EVENTS tab.
 *
 * DARK: a dark forest with lamp posts (bg-shadowarena) and three white-outlined
 * cards — Horror Hambi, Demonic Hambi, The Ham Reaper — bought with Dark Hambi
 * Coins. LIGHT (spec 2026-10-13): a sunset sky turning to night (bg-lightshop)
 * and three white cards outlined in black — Healer Hambi, Banking Burger,
 * Angelic Hambi — bought with Light Hambi Coins. BACK returns to the EVENTS tab.
 */
export class OriginShopScene extends Phaser.Scene {
  private shop: OriginShopId = 'dark';
  private progress!: ProgressService;
  private balanceText!: Phaser.GameObjects.Text;
  private cards: { tower: OriginShopTower; refresh: () => void }[] = [];
  private notice?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'OriginShopScene' });
  }

  init(data: { shop?: OriginShopId }): void {
    this.shop = data?.shop === 'light' ? 'light' : 'dark';
  }

  create(): void {
    this.progress = hubProgress();
    this.cards = [];
    this.notice = undefined;
    const dark = this.shop === 'dark';
    const cx = 640;

    const bgKey = dark ? 'bg-shadowarena' : 'bg-lightshop';
    if (this.textures.exists(bgKey)) {
      this.add.image(640, 360, bgKey).setDisplaySize(1280, 720).setDepth(-2);
    }
    // The forest is dimmed so the white outlines read; the sky stays bright.
    if (dark) this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.35).setDepth(-1);

    this.add
      .text(cx, 52, dark ? 'DARK SHOP' : 'LIGHT SHOP', {
        fontSize: '40px',
        // White on both: the Light Shop's sky is night at the top too.
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: dark ? '#000000' : '#1a1420',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setName('origin-shop-title');
    this.add
      .text(cx, 92, 'THE HUB · ORIGIN EVENT', {
        fontSize: '15px',
        color: '#d9d4e6',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setName('origin-shop-back')
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => this.scene.start('HubScene', { tab: 'events' }));

    // The shop's currency, where the chapter screens keep theirs.
    const coinIcon = this.add
      .image(960, 50, dark ? 'darkcoin' : 'lightcoin')
      .setDisplaySize(34, 34)
      .setDepth(5);
    spinCoin(this, coinIcon, dark ? DARKCOIN_SPIN_FRAMES : LIGHTCOIN_SPIN_FRAMES);
    this.balanceText = this.add
      .text(982, 50, '', {
        fontSize: '22px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0, 0.5)
      .setDepth(5)
      .setName('origin-shop-balance');
    this.showBalance();

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => music.stop());

    originShopShelf(this.shop).towers.forEach((t, i) => this.makeCard(310 + i * 330, 410, t));

    installDebugReturn(this);
  }

  private showBalance(): void {
    this.balanceText.setText(String(this.balance()));
  }

  private balance(): number {
    return this.shop === 'dark' ? this.progress.darkCoins() : this.progress.lightCoins();
  }

  /** Held already: bought here, or handed over another way (e.g. /give tower). */
  private owns(t: OriginShopTower): boolean {
    return availableTowers(this.progress.enteredLevels(), this.progress.unlockedExtras()).includes(
      t,
    );
  }

  /**
   * One card: the tower, what it does, its price and BUY. The Dark Shop's are
   * near-black outlined in white; the Light Shop's the reverse — white outlined
   * in black, with dark text — like the buttons that open them.
   */
  private makeCard(x: number, y: number, tower: OriginShopTower): void {
    const dark = this.shop === 'dark';
    const ink = dark ? 0xffffff : 0x14101c;
    const inkHex = dark ? '#ffffff' : '#14101c';
    const w = 300;
    const h = 440;
    const top = y - h / 2;
    this.add
      .rectangle(x, y, w, h, dark ? 0x07070c : 0xffffff, dark ? 0.82 : 0.9)
      .setStrokeStyle(3, ink)
      .setName(`origin-shop-card-${tower}`);
    const art = `tower-${tower}-1`;
    if (this.textures.exists(art)) this.add.image(x, top + 92, art).setDisplaySize(140, 140);
    this.add
      .text(x, top + 186, TOWER_NAMES[tower], {
        fontSize: '24px',
        color: inkHex,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.add
      .text(x, top + 214, TOWER_TUTORIALS[tower], {
        fontSize: '14px',
        color: dark ? '#c9c9d4' : '#3a3346',
        align: 'center',
        wordWrap: { width: w - 34 },
        lineSpacing: 3,
      })
      .setOrigin(0.5, 0);

    const price = originShopShelf(this.shop).prices[tower];
    const priceY = top + h - 92;
    const coin = this.add
      .image(x - 30, priceY, dark ? 'darkcoin' : 'lightcoin')
      .setDisplaySize(28, 28);
    const priceText = this.add
      .text(x - 10, priceY, String(price), {
        fontSize: '24px',
        color: inkHex,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setName(`origin-shop-price-${tower}`);

    const btnY = top + h - 40;
    const btn = this.add.graphics();
    const label = this.add
      .text(x, btnY, '', { fontSize: '22px', fontStyle: 'bold', color: inkHex })
      .setOrigin(0.5)
      .setName(`origin-shop-buy-label-${tower}`);
    const hit = this.add
      .zone(x, btnY, 200, 46)
      .setName(`origin-shop-buy-${tower}`)
      .setInteractive({ useHandCursor: true });
    let hover = false;
    const refresh = () => {
      const owned = this.owns(tower);
      const afford = this.balance() >= price;
      btn.clear();
      if (dark) btn.fillStyle(owned ? 0x1e3a24 : hover && afford ? 0x2a2a36 : 0x111118, 1);
      else btn.fillStyle(owned ? 0xd6f0dc : hover && afford ? 0xe8e4f0 : 0xffffff, 1);
      btn.fillRoundedRect(x - 100, btnY - 23, 200, 46, 10);
      btn.lineStyle(2, ink, owned ? 0.5 : afford ? 1 : 0.4);
      btn.strokeRoundedRect(x - 100, btnY - 23, 200, 46, 10);
      label
        .setText(owned ? 'OWNED ✓' : 'BUY')
        .setColor(owned || afford ? inkHex : dark ? '#7d7d8a' : '#9a96a4');
      coin.setVisible(!owned);
      priceText.setVisible(!owned);
      if (owned) hit.disableInteractive();
    };
    hit.on('pointerover', () => {
      hover = true;
      refresh();
    });
    hit.on('pointerout', () => {
      hover = false;
      refresh();
    });
    hit.on('pointerup', () => this.buy(tower));
    this.cards.push({ tower, refresh });
    refresh();
  }

  private buy(tower: OriginShopTower): void {
    if (this.owns(tower)) return;
    const dark = this.shop === 'dark';
    const price = originShopShelf(this.shop).prices[tower];
    const bought = dark
      ? this.progress.purchaseTowerWithDarkCoins(tower, price)
      : this.progress.purchaseTowerWithLightCoins(tower, price);
    if (!bought) {
      const coins = dark ? 'Dark Hambi Coins' : 'Light Hambi Coins';
      this.flash(`Not enough ${coins} — ${TOWER_NAMES[tower]} costs ${price}.`, '#ff9a9a');
      return;
    }
    getAudioService(this).playEvent({ type: 'towerPlaced', towerType: tower });
    this.showBalance();
    for (const c of this.cards) c.refresh();
    this.flash(`${TOWER_NAMES[tower]} is yours! Equip him from the Equip screen.`, '#ffffff');
    // The third tower of a shop earns Ghosting / Halo Hunting.
    sweepAndToastAchievements(saveStorage());
  }

  private flash(text: string, color: string): void {
    this.notice?.destroy();
    const n = this.add
      .text(640, 668, text, {
        fontSize: '18px',
        color,
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setName('origin-shop-notice');
    this.notice = n;
    this.time.delayedCall(2600, () => {
      if (this.notice === n) this.notice = undefined;
      n.destroy();
    });
  }
}
